import { createClient } from '@supabase/supabase-js';
import {
  freeAllDisposableLocalStorageCaches,
  freeDisposableLocalStorageCaches,
  isLocalStorageQuotaError,
} from './utils/localStorageQuota';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL as string;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY as string;

if (!supabaseUrl || !supabaseAnonKey) {
  // 런타임에서 환경변수 누락을 빠르게 찾기 위한 방어 코드
  // 실제 서비스에서는 콘솔 에러 정도로만 두어도 충분합니다.
  throw new Error('Supabase 환경변수가 설정되지 않았습니다. .env.local을 확인하세요.');
}

/**
 * 평소에는 캐시를 유지한다.
 * 인증 토큰 setItem이 quota로 실패할 때만 큰 캐시부터 최소 확보 후 재시도한다.
 */
const authStorage = {
  getItem: (key: string) => {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem: (key: string, value: string) => {
    try {
      window.localStorage.setItem(key, value);
    } catch (err) {
      if (!isLocalStorageQuotaError(err)) throw err;
      freeDisposableLocalStorageCaches();
      try {
        window.localStorage.setItem(key, value);
      } catch (err2) {
        if (!isLocalStorageQuotaError(err2)) throw err2;
        freeAllDisposableLocalStorageCaches();
        window.localStorage.setItem(key, value);
      }
    }
  },
  removeItem: (key: string) => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  },
};

/**
 * 브라우저는 auth 세션(localStorage) 동시 접근을 막기 위해 Web Locks API(`lock:sb-…-auth-token`)를 씁니다.
 * React Strict Mode(개발)는 마운트→언마운트→재마운트를 시뮬레이션해 잠금이 5초 안에 풀리지 않은 것처럼 보일 수 있고,
 * @supabase/gotrue-js가 타임아웃 후 steal로 복구하면서 콘솔 경고가 납니다. 동작 자체는 복구된 것이며,
 * 프로덕션 빌드에서는 Strict Mode 이중 호출이 없어 같은 경고가 거의 나지 않습니다.
 *
 * 개발 중에만 잠금을 생략하면 해당 경고를 피할 수 있습니다(단일 탭 개발 기준). 여러 탭에서 동시에 같은 계정으로
 * 편집하는 경우에는 프로덕션과 동일하게 브라우저 잠금을 쓰는 편이 안전합니다.
 */
const authOptions = {
  storage: authStorage,
  ...(process.env.NODE_ENV === 'development'
    ? {
        lock: async <R,>(
          _name: string,
          _acquireTimeout: number,
          fn: () => Promise<R>
        ): Promise<R> => fn(),
      }
    : {}),
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: authOptions,
});

export const SUPABASE_URL = supabaseUrl;
export const SUPABASE_ANON_KEY = supabaseAnonKey;
